import React, { Component } from 'react';
import Select from './common/select';
import { HTTPService } from "./../services/httpService";

const API = new HTTPService()

export default class SpecialistForm extends Component {
    // Instance variable to store display name to specialist mapping
    specialistDisplayMap = {};

    state = {
        data : {phone : ""},
        errors : {},
        name : "Select the specialist",
        email : "",
        phone : "",
        found : " ",
        customerId: "",
        selectedSpecialist:"Specialist",
        specialists:[],
        // Vendor category state
        selectedVendorCategory: "All Categories",
        vendorCategories: [],
        vendorVisibility: "all", // "preferred" or "all"
        filteredSpecialists: [],
        // Loading states
        loadingCategories: false,
        loadingSpecialists: false,
        // Preferred specialist from dealer preferences
        preferredSpecialistFromDealer: null,
        preferredLocationFromDealer: null
    };

    // Fetch vendor categories for the dealership
    fetchVendorCategories = async () => {
        this.setState({ loadingCategories: true });
        try {
            const response = await API.get(`/dealer/preferences/${this.props.dealerId}`);
            
            // Extract preferred specialist + location if available
            let preferredSpecialist = null;
            let preferredLocation = null;
            if (response.data.length > 0) {
                // Look for default vendor in any category
                for (const category of response.data) {
                    if (category.default_vendor_id) {
                        preferredSpecialist = category.default_vendor_id;
                        preferredLocation = category.default_vendor_location_id || null;
                        break;
                    }
                }
            }

            this.setState({
                vendorCategories: response.data,
                loadingCategories: false,
                preferredSpecialistFromDealer: preferredSpecialist,
                preferredLocationFromDealer: preferredLocation
            });
            
            // If there are categories, set the first one as default and fetch specialists
            if (response.data.length > 0) {
                const defaultCategory = response.data[0];
                this.setState({
                    selectedVendorCategory: defaultCategory.category_name,
                    vendorVisibility: defaultCategory.vendor_visibility_mode
                }, () => {
                    this.fetchSpecialistsByCategory(defaultCategory.category_name);
                });
            } else {
                // No categories defined, fetch all specialists
                this.fetchSpecialistsByCategory("All Categories");
            }
        } catch (error) {
            console.error("Error fetching vendor categories:", error);
            this.setState({ loadingCategories: false });
            // Fallback to all specialists
            this.fetchSpecialistsByCategory("All Categories");
        }
    };

    // Fetch specialists filtered by category from server
    fetchSpecialistsByCategory = async (categoryName) => {
        this.setState({ loadingSpecialists: true });
        try {
            const endpoint = categoryName === "All Categories" 
                ? `/installerProximity/${this.props.dealerId}`
                : `/installerProximity/${this.props.dealerId}/${encodeURIComponent(categoryName)}`;
            
            const response = await API.get(endpoint);

            // Store all specialists - filtering will be applied in render based on context
            // When visibility is "preferred", only preferred specialists will be shown
            // When visibility is "all", "Any" shows all specialists, but individual options show only preferred
            let filteredSpecialists = response.data;
            
            // If visibility mode is 'preferred', filter to only show dealer_preferred = 1
            // NB: server serializes dealer_preferred as a string ("1"), so coerce before comparing.
            if (this.state.vendorVisibility === "preferred") {
                filteredSpecialists = response.data.filter(specialist => Number(specialist.dealer_preferred) === 1);
            }
            
            // Look up preferred vendor/location for the current category
            const categoryConfig = this.state.vendorCategories.find(cat => cat.category_name === categoryName);
            const catVendorId = categoryConfig?.default_vendor_id || this.state.preferredSpecialistFromDealer;
            const catLocationId = categoryConfig?.default_vendor_location_id || this.state.preferredLocationFromDealer;

            this.setState({
                filteredSpecialists: filteredSpecialists,
                loadingSpecialists: false,
                // Reset specialist selection when category changes
                selectedSpecialist: "Specialist",
                name: "Select the specialist",
                preferredSpecialistFromDealer: catVendorId,
                preferredLocationFromDealer: catLocationId
            }, () => {
                // Auto-select preferred specialist if available
                this.autoSelectPreferredSpecialist();
            });
        } catch (error) {
            console.error("Error fetching specialists:", error);
            this.setState({ 
                filteredSpecialists: [],
                loadingSpecialists: false,
                selectedSpecialist: "Specialist",
                name: "Select the specialist"
            });
        }
    };

    // Select a specialist programmatically (shared by auto-select and single-option logic)
    selectSpecialist = (specialist) => {
        const baseName = specialist.Installer_Name || specialist.installer_name;
        const locationLabel = specialist.location_name || specialist.locationName || specialist.location_city;
        const parts = [locationLabel ? `${baseName} - ${locationLabel}` : baseName];

        const isDealer = this.props.userType === 'dealer';

        if (isDealer && specialist.Distance !== null && specialist.Distance !== undefined) {
            parts.push(`${Number(specialist.Distance).toFixed(1)} mi`);
        }

        if (isDealer) {
            if (specialist.google_rating && specialist.google_review_count) {
                parts.push(`⭐ ${Number(specialist.google_rating).toFixed(1)} (${specialist.google_review_count})`);
            } else if (specialist.google_rating) {
                parts.push(`⭐ ${Number(specialist.google_rating).toFixed(1)}`);
            }
        }

        const displayName = parts.join(' • ');

        this.setState({
            selectedSpecialist: displayName,
            name: baseName
        });

        const locId = specialist.location_id || specialist.Location_id || null;
        this.props.handleSpecialist(specialist.Installer_id, this.state.selectedVendorCategory, locId);
    };

    // Auto-select preferred specialist if found, or auto-select if only one option
    autoSelectPreferredSpecialist = () => {
        const preferredSpecialists = this.state.filteredSpecialists.filter(specialist => Number(specialist.dealer_preferred) === 1);

        // Try to match the dealer's configured preferred vendor+location
        if (this.state.preferredSpecialistFromDealer) {
            let foundPreferred = null;
            if (this.state.preferredLocationFromDealer) {
                foundPreferred = preferredSpecialists.find(specialist =>
                    String(specialist.Installer_id) === String(this.state.preferredSpecialistFromDealer) &&
                    String(specialist.location_id || specialist.Location_id) === String(this.state.preferredLocationFromDealer)
                );
            }
            if (!foundPreferred) {
                foundPreferred = preferredSpecialists.find(specialist =>
                    String(specialist.Installer_id) === String(this.state.preferredSpecialistFromDealer)
                );
            }
            if (foundPreferred) {
                this.selectSpecialist(foundPreferred);
                return;
            }
        }

        // Count total selectable options: individual preferred + "Any" (if visible)
        const hasAnyOption = this.state.vendorVisibility === "all" && this.state.filteredSpecialists.length > 0;
        const totalOptions = preferredSpecialists.length + (hasAnyOption ? 1 : 0);

        // If only one selectable option, auto-select it to speed up data entry
        if (totalOptions === 1) {
            if (preferredSpecialists.length === 1) {
                this.selectSpecialist(preferredSpecialists[0]);
            } else if (hasAnyOption) {
                // "Any" is the only option — select it
                const anyLabel = `Any (${this.state.filteredSpecialists.length})`;
                this.setState({ selectedSpecialist: anyLabel, name: 'Any' });
                this.props.handleSpecialist("0", this.state.selectedVendorCategory);
            }
        }
    };

    // Handle vendor category selection
    handleVendorCategoryChange = async (event) => {
        const { value } = event.target;
        
        // Find the category configuration to get vendor_visibility_mode
        const categoryConfig = this.state.vendorCategories.find(cat => cat.category_name === value);
        const visibility = categoryConfig ? categoryConfig.vendor_visibility_mode : "all";
        
        this.setState({ 
            selectedVendorCategory: value,
            vendorVisibility: visibility,
            selectedSpecialist: "Specialist",
            name: "Select the specialist"
        });

        // Fetch specialists for the selected category
        await this.fetchSpecialistsByCategory(value);

        // Notify parent component about category change
        if (this.props.handleSpecialistCategory) {
            this.props.handleSpecialistCategory(value, visibility);
        }
    };

    // Component lifecycle - fetch categories when component mounts
    componentDidMount() {
        if (this.props.dealerId) {
            this.fetchVendorCategories();
        } else {
            console.warn("SpecialistForm: dealerId prop is required");
        }
    }

    // Handle specialist selection
    handleSpecialistChange = (event) => {
        const { value } = event.target;
        //console.log ("Selected Specialist:", value);
        //console.log("Available Specialists:", this.state.filteredSpecialists);

        let returnValue = "0";
        let cleanedValue = value;

        // Check if the selection is "Any" with count, and clean it
        if (value.startsWith("Any (") && value.endsWith(")")) {
            cleanedValue = "Any";
            this.setState({ name: cleanedValue, selectedSpecialist: value });
            this.props.handleSpecialist(returnValue, this.state.selectedVendorCategory);
            return;
        }

        // Handle "Specialist" placeholder selection
        if (value === "Specialist") {
            this.setState({ name: "Select the specialist", selectedSpecialist: value });
            this.props.handleSpecialist(returnValue, this.state.selectedVendorCategory);
            return;
        }

        // Look up the specialist object from the display map
        const specialist = this.specialistDisplayMap[value];

        if (specialist) {
            // Extract the actual name without distance/rating info
            const actualName = specialist.Installer_Name || specialist.installer_name;
            returnValue = specialist.Installer_id || specialist.installer_id;

            this.setState({
                name: actualName,
                selectedSpecialist: value
            });

            // Notify parent component — pass location_id if present (multi-location discovery)
            this.props.handleSpecialist(returnValue, this.state.selectedVendorCategory, specialist.location_id || specialist.Location_id || null);
        } else {
            // Fallback: try to find by exact name match (for backwards compatibility)
            const installer = this.state.filteredSpecialists.find(special => special.Installer_Name === value);
            if (installer) {
                returnValue = installer.Installer_id;
            }

            this.setState({ name: value, selectedSpecialist: value });
            this.props.handleSpecialist(returnValue, this.state.selectedVendorCategory);
        }
    };

    // Helper method to build specialist display list and map
    buildSpecialistDisplayList = (specialists) => {
        // Sort by distance (closest first)
        const sortedSpecialists = specialists
            .filter(specialist => Number(specialist.dealer_preferred) === 1)
            .sort((a, b) => {
                const distA = a.Distance !== null && a.Distance !== undefined ? a.Distance : 999;
                const distB = b.Distance !== null && b.Distance !== undefined ? b.Distance : 999;
                return distA - distB;
            });

        // Check if user is a dealer
        const isDealer = this.props.userType === 'dealer';

        // Create display map for reverse lookup and format display strings
        const displayMap = {};
        const displayList = sortedSpecialists.map(specialist => {
            const baseName = specialist.Installer_Name || specialist.installer_name;
            const locationLabel = specialist.location_name || specialist.locationName || specialist.location_city;
            const parts = [locationLabel ? `${baseName} - ${locationLabel}` : baseName];

            // Add distance in miles - for dealer users
            if (isDealer && specialist.Distance !== null && specialist.Distance !== undefined) {
                parts.push(`${Number(specialist.Distance).toFixed(1)} mi`);
            }

            // Add Google rating and review count - for dealer users
            if (isDealer) {
                if (specialist.google_rating && specialist.google_review_count) {
                    parts.push(`⭐ ${Number(specialist.google_rating).toFixed(1)} (${specialist.google_review_count})`);
                } else if (specialist.google_rating) {
                    parts.push(`⭐ ${Number(specialist.google_rating).toFixed(1)}`);
                }
            }

            const displayName = parts.join(' • ');
            displayMap[displayName] = specialist; // Store mapping
            return displayName;
        });

        return { displayList, displayMap };
    };

    render() {
        if (this.props.currentStep !== 1) {
            return null;
        }

        // Build specialist options from filtered list
        let specialistList;

        if (this.state.loadingSpecialists) {
            specialistList = ["Loading..."];
            this.specialistDisplayMap = {}; // Clear the map when loading
        } else {
            // Build the display list and map
            const result = this.buildSpecialistDisplayList(this.state.filteredSpecialists);
            specialistList = result.displayList;
            this.specialistDisplayMap = result.displayMap; // Store in instance variable
        }
        
        // Add "Any" option with count of ALL specialists only if vendor_visibility_mode allows it and not loading
        if (!this.state.loadingSpecialists && this.state.vendorVisibility === "all" && this.state.filteredSpecialists.length > 0) {
            const anyOptionWithCount = `Any (${this.state.filteredSpecialists.length})`;
            specialistList.unshift(anyOptionWithCount);
        }
        
        if (!this.state.loadingSpecialists) {
            specialistList.unshift("Specialist");
        }

        // Build category options
        const categoryOptions = this.state.loadingCategories 
            ? ["Loading..."]
            : ["All Categories", ...this.state.vendorCategories.map(cat => cat.category_name)];

        // Dynamic customer message based on current state
        let customerMessage;
        if (this.state.loadingSpecialists || this.state.loadingCategories) {
            customerMessage = "Loading specialists...";
        } else if (this.state.filteredSpecialists.length === 0) {
            customerMessage = "No specialists available";
        } else if (this.state.vendorVisibility === "preferred") {
            customerMessage = "Select your preferred specialist";
        } else if (this.state.vendorVisibility === "all") {
            customerMessage = "Select your preferred specialist or ANY";
        } else {
            // Default fallback - before any category is selected or processed
            customerMessage = "Select your specialist";
        }
        
        return (
            <React.Fragment>
                <form className="form-container p-5">
                    <div className="row justify-content-md-center">
                        <h5 className="text-center">{customerMessage}</h5>
                    </div>
                    
                    {/* Vendor Category Dropdown - Only show if more than 1 category */}
                    {!this.state.loadingCategories && this.state.vendorCategories.length > 1 && (
                        <div className="form-row justify-content-md-center">
                            <div className="form-group col-md-3 text-center">
                                <Select 
                                    name="vendorCategory"
                                    label="Vendor Category"
                                    options={categoryOptions}
                                    value={this.state.selectedVendorCategory}
                                    onChange={this.handleVendorCategoryChange}
                                />
                            </div>
                        </div>
                    )}

                    {/* Specialist Dropdown - REMOVED LABEL */}
                    <div className="form-row justify-content-md-center">
                        <div className="form-group col-md-3 text-center">
                            <Select 
                                name="specialist"
                                options={specialistList}
                                value={this.state.selectedSpecialist}
                                onChange={this.handleSpecialistChange}
                                disabled={this.state.loadingSpecialists || this.state.loadingCategories}
                            />
                        </div>
                    </div>

                    {/* Loading indicator */}
                    {this.state.loadingSpecialists && (
                        <div className="row justify-content-md-center">
                            <div className="col-md-6 text-center">
                                <small className="text-muted">
                                    Loading specialists...
                                </small>
                            </div>
                        </div>
                    )}

                    {/* No specialists found message */}
                    {!this.state.loadingSpecialists && this.state.filteredSpecialists.length === 0 && (
                        <div className="row justify-content-md-center">
                            <div className="col-md-6 text-center">
                                <small className="text-warning">
                                    No specialists found for the selected category.
                                </small>
                            </div>
                        </div>
                    )}
                </form>

                {(this.state.selectedSpecialist !== "Specialist" && 
                  this.state.selectedSpecialist !== "Loading...") && (
                    <div>
                        <h5 className="text-center bold p-5">Specialist Selected</h5>
                        <div className="col-lg-12 bold text-center">
                            <p>{this.state.name}</p>
                            <p>{this.state.email}</p>
                        </div>
                    </div>
                )}
            </React.Fragment>
        );
    }
}